import { ColDef } from 'ag-grid-community';
import type { Asset, CsvResponse, IUnderlyingUniverse } from 'apiConnector/underlying-universe';
import { DropdownWrapper, HeightLimitedDropdownMenu, SpDropdownItem } from 'components/shared/Dropdown';
import { commodityColumnDefinitions } from 'components/underlyingUniverse/columnDefinitions/commodityColumnDefinitions';
import { creditColumnDefinitions } from 'components/underlyingUniverse/columnDefinitions/creditColumnDefinitions';
import { getEquityColumnDefinitions } from 'components/underlyingUniverse/columnDefinitions/equityColumnDefinitions';
import {
  externalUserForexColumnDefinitions,
  InternalSalesForexColumnDefinitions,
} from 'components/underlyingUniverse/columnDefinitions/forexColumnDefinitions';
import { rateColumnDefinitions } from 'components/underlyingUniverse/columnDefinitions/rateColumnDefinitions';
import type { AnalyticCategoryActions } from 'models/analytics';
import { FC, useMemo, useState } from 'react';
import { DropdownToggle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import type { Observable } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { ExportCsvButton, UnderlyingUniverseGrid, UnderlyingUniverseSearch } from './UnderlyingUniverseGrid';
import { WithUnderlyingUniverseSearchContext } from './UnderlyingUniverseGrid.context';

interface Props {
  data: IUnderlyingUniverse;
  isInternal?: boolean;
  fetchCSV(asset: Asset, query?: string): Observable<CsvResponse>;
}

const underlyingTypes = ['Equity', 'Commodity', 'CreditRate', 'Forex', 'Rate'] satisfies readonly Asset[];

const underlyingTypeToDataField: Record<Asset, keyof IUnderlyingUniverse & `${string}Underlyings`> = {
  Commodity: 'commodityUnderlyings',
  CreditRate: 'creditRateUnderlyings',
  Equity: 'equityUnderlyings',
  Forex: 'forexUnderlyings',
  Rate: 'rateUnderlyings',
};

const underlyingTypeToColDefs: Record<Asset, (isInternal?: boolean) => ColDef[]> = {
  Commodity: () => commodityColumnDefinitions,
  CreditRate: () => creditColumnDefinitions,
  Equity: isInternal => getEquityColumnDefinitions(isInternal),
  Forex: isInternal => (isInternal ? InternalSalesForexColumnDefinitions : externalUserForexColumnDefinitions),
  Rate: () => rateColumnDefinitions,
};

export const UnderlyingUniversePresentation: FC<Props> = ({ data, isInternal, fetchCSV }) => {
  const [activeAsset, setActiveAsset] = useState<Asset>('Equity');

  const columns = useMemo(() => {
    const cols = underlyingTypeToColDefs[activeAsset](isInternal);
    return cols;
  }, [isInternal, activeAsset]);

  return (
    <WithUnderlyingUniverseSearchContext asset={activeAsset}>
      <div className="h-100 d-flex flex-column position-relative border-top" data-e2e="underlying-universe">
        <div className="d-flex flex-row flex-wrap flex-md-nowrap gap-1">
          <div className="d-flex flex-grow-1 px-16px align-items-center">
            <h4 className="h5 lh-1">
              <FormattedMessage id="underlyingUniverse" />
            </h4>
          </div>
          <DropdownWrapper className="btn-group border-start border-opacity-40" openOnHover>
            <DropdownToggle
              variant="link"
              aria-haspopup="listbox"
              data-e2e="asset-filter"
              aria-label="asset filter"
              className="btn btn-flat-secondary btn-lg btn-icon-start dropdown-toggle pe-2"
            >
              <i className="icon" aria-hidden={false}>
                category
              </i>
              <span className="d-none d-lg-inline">
                <FormattedMessage id={`underlyingUniverse.${activeAsset}`} />
              </span>
            </DropdownToggle>
            <HeightLimitedDropdownMenu role="listbox">
              {underlyingTypes
                .filter(underlyingType => underlyingType !== 'Commodity' || data?.commodityUnderlyings)
                .map(underlyingType => (
                  <SpDropdownItem
                    active={underlyingType === activeAsset}
                    key={underlyingType}
                    onClick={() => {
                      if (underlyingType !== activeAsset) {
                        setActiveAsset(underlyingType);

                        logAnalytics.trackEvent('Underlying universe', tabSelectionAnalyticEvents[underlyingType]);
                      }
                    }}
                  >
                    <FormattedMessage id={`underlyingUniverse.${underlyingType}`} />
                  </SpDropdownItem>
                ))}
            </HeightLimitedDropdownMenu>
          </DropdownWrapper>
          <UnderlyingUniverseSearch />
          <ExportCsvButton asset={activeAsset} fetchCSV={fetchCSV} />
        </div>
        <UnderlyingUniverseGrid
          key={activeAsset}
          underlyings={data?.[underlyingTypeToDataField[activeAsset]]}
          columnDefs={columns}
        />
      </div>
    </WithUnderlyingUniverseSearchContext>
  );
};

UnderlyingUniversePresentation.displayName = 'UnderlyingUniversePresentation';

const tabSelectionAnalyticEvents: Record<Asset, AnalyticCategoryActions['Underlying universe']> = {
  Equity: 'Select Equities tab',
  Commodity: 'Select Commodities tab',
  CreditRate: 'Select Credit tab',
  Forex: 'Select Forex tab',
  Rate: 'Select Rate tab',
};
